import * as React from 'react';
import { createLocators } from '@u1/locators';
import clsx from 'clsx';

import { Checked, Indeterminate, Unchecked } from './CheckIcons';
import { Typography } from '../Typography';

import { AnimatePresence } from 'framer-motion';
import useStyles from './Checkbox.styles';

export interface ICheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Text or React element displayed next to the checkbox
   */
  label?: React.ReactNode;
  /**
   * The name of the input element.
   */
  name?: string;
  /**
   * Checkbox value
   */
  value?: string;
  /**
   * Triggers checked state
   */
  checked?: boolean;
  /**
   * Triggers disabled state
   */
  disabled?: boolean;
  /**
   * Triggers indeterminate state. If both 'intermediate' and 'checked' states are active, the result state will be 'checked'
   */
  indeterminate?: boolean;
  /**
   * HTML input onChange callback. 'Value' and 'checked' coud be extracted from the event target. {value, checked} = e.target
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * React ref for checkbox
   */
  inputRef?: React.Ref<HTMLLabelElement>;
  /**
   * Adds default left margin for checkbox
   */
  nested?: boolean;
  /**
   * Escape hatch to add styles to the checkbox wrapper
   */
  _UNSAFE_classname?: string;
}

export const checkboxLocators = createLocators({
  name: 'Checkbox',
  componentLocators: [
    'checkbox',
    'label',
    'checkboxWrap',
    'checkbox',
    'iconUnchecked',
    'iconChecked',
    'iconIntermediate',
    'input',
  ],
});
const { locators } = checkboxLocators();

const Checkbox: React.FC<ICheckboxProps> = ({
  name,
  checked,
  label,
  value,
  disabled,
  onChange,
  inputRef,
  indeterminate,
  nested,
  _UNSAFE_classname,
  ...rest
}) => {
  const classes = useStyles();

  return (
    <label
      {...locators.label({ disabled, checked, indeterminate })}
      ref={inputRef}
      tabIndex={0}
      className={clsx(_UNSAFE_classname, {
        [classes.root]: true,
        [classes.disabled]: disabled,
        [classes.nested]: nested,
      })}
    >
      <input
        {...locators.input()}
        {...rest}
        type="checkbox"
        name={name}
        value={value}
        onChange={onChange}
        checked={checked}
        disabled={disabled}
        className={classes.input}
      />

      <div {...locators.checkboxWrap()} className={classes.checkboxWrap}>
        <div {...locators.checkbox()} className={classes.checkbox}>
          <Unchecked
            locatorsOverrides={locators.iconUnchecked()}
            className={classes.unchecked}
          />
          <AnimatePresence initial={false}>
            {checked && (
              <Checked
                locatorsOverrides={locators.iconChecked({ checked })}
                {...locators.iconChecked()}
                className={clsx({
                  [classes.icon]: true,
                })}
              />
            )}
            {indeterminate && !checked && (
              <Indeterminate
                locatorsOverrides={locators.iconIntermediate({
                  checked: indeterminate && !checked,
                })}
                className={clsx({
                  [classes.icon]: true,
                })}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {label ? (
        <Typography
          locatorsOverrides={locators.label()}
          className={classes.label}
          as="span"
        >
          {label}
        </Typography>
      ) : null}
    </label>
  );
};

export default Checkbox;
